import { z } from "zod";

// Base schemas for data validation
export const typeSchema = z.object({
  // key: z.string().regex(/^[A-Za-z][A-Za-z0-9_ -]{0,63}$/, "Type key must start with letter and contain only alphanumeric, underscore, space, or dash"),
  key: z.string(),
  code: z.string().optional(),
  name: z.string().min(1).max(200).transform(s => s.trim().replace(/\s+/g, ' ')),
  description: z.string().max(500).optional(),
  displayName: z.string().max(200).optional(),
  numericCode: z.string().optional(),
  seq: z.number().int().min(0).default(0),
  useFlag: z.boolean().default(true),
  type: z.string().optional(),
  parentType: z.string().optional(),
  parentCode: z.string().optional(),
  effectiveStart: z.string().datetime().optional(),
  effectiveEnd: z.string().datetime().optional(),
  createdAt: z.string().datetime().default(() => new Date().toISOString()),
  createdBy: z.string().default("system"),
  isDeleted: z.boolean().default(false),
  childTypesCount: z.number().default(0),
  hasChildTypes: z.boolean().default(false),
});

export const codeSchema = z.object({
  type: z.string(),
  code: z.string().regex(/^[A-Za-z0-9_./-]{1,64}$/, "Code must be 1-64 characters, alphanumeric with . _ / -"),
  name: z.string().min(1).max(200).transform(s => s.trim().replace(/\s+/g, ' ')),
  description: z.string().max(500).optional(),
  parentType: z.string().optional(),
  parentCode: z.string().optional(),
  seq: z.number().int().min(0).default(0),
  useFlag: z.number().int().min(0).default(0),
  // useFlag: z.boolean().default(true),
  effectiveStart: z.string().datetime().optional(),
  effectiveEnd: z.string().datetime().optional(),
  createdAt: z.string().datetime().default(() => new Date().toISOString()),
  createdBy: z.string().default("system"),
  isDeleted: z.boolean().default(false),
  childTypesCount: z.number().default(0),
  hasChildTypes: z.boolean().default(false),
});

export const auditEntrySchema = z.object({
  id: z.string(),
  timestamp: z.string().datetime(),
  action: z.enum(["CREATE", "UPDATE", "DELETE", "IMPORT", "EXPORT"]),
  entityType: z.enum(["TYPE", "CODE"]),
  entityKey: z.string(),
  oldValue: z.record(z.any()).optional(),
  newValue: z.record(z.any()).optional(),
  user: z.string(),
  note: z.string().optional()
});

export const headersSchema = z.record(z.string());

export const errorTypeSchema = z.enum([
  "AUTH_ERROR",
  "DATABASE_ERROR",
]);

export const requestSchema = z.object({
  method: z.string(),
  path: z.string(),
  query_params: z.record(z.string()).optional(),
  headers: headersSchema,
});

export const apiErrorEntrySchema  = z.object({
  timestamp: z.string().datetime(),
  error_type: errorTypeSchema,
  status_code: z.number(),
  error_message: z.string(),
  error_details: z.string().optional(),
  response_time_ms: z.number().optional(),
  request: requestSchema,
  ip_address: z.string(),
  user_agent: z.string(),
});

export const auditDataSchema = z.object({
  entries: z.array(apiErrorEntrySchema),
});

export const settingsSchema = z.object({
  canonicalizeCode: z.boolean().default(true),
  enforceUniqueNames: z.boolean().default(true),
  nlpProvider: z.enum(["LocalRules", "NLPS_API", "Proxy"]).default("LocalRules"),
  apiEndpoint: z.string().url().optional(),
  autoSave: z.boolean().default(true),
  theme: z.enum(["light", "dark", "system"]).default("light")
});

export const aiQuerySchema = z.object({
  id: z.string(),
  query: z.string(),
  intent: z.string().optional(),
  entities: z.record(z.any()).optional(),
  confidence: z.number().min(0).max(1).optional(),
  result: z.any().optional(),
  timestamp: z.string().datetime(),
  user: z.string(),
  saved: z.boolean().default(false),
  name: z.string().optional()
});

// Root storage schema
export const storageSchema = z.object({
  types: z.array(typeSchema).default([]),
  codes: z.array(codeSchema).default([]),
  audit: z.array(auditEntrySchema).default([]),
  settings: settingsSchema.default({}),
  ai: z.object({
    queries: z.array(aiQuerySchema).default([]),
    savedQueries: z.array(aiQuerySchema).default([])
  }).default({ queries: [], savedQueries: [] })
});

// Export types
export type Type = z.infer<typeof typeSchema>;
export type Code = z.infer<typeof codeSchema>;
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export type AuditData = z.infer<typeof auditDataSchema>;
export type Settings = z.infer<typeof settingsSchema>;
export type AIQuery = z.infer<typeof aiQuerySchema>;
export type StorageData = z.infer<typeof storageSchema>;

// Insert schemas
export const insertTypeSchema = typeSchema.omit({ createdAt: true, isDeleted: true });
// For inserts, code is auto-assigned (numeric per parent group). Allow omitting 'code'.
export const insertCodeSchema = codeSchema.partial({ code: true }).omit({ createdAt: true, isDeleted: true });
export const insertAuditEntrySchema = auditEntrySchema.omit({ timestamp: true });

export type InsertType = z.infer<typeof insertTypeSchema>;
export type InsertCode = z.infer<typeof insertCodeSchema>;
export type InsertAuditEntry = z.infer<typeof insertAuditEntrySchema>;

export const typeEdgeSchema = z.object({
  fromType: z.string(),
  toType: z.string(),
  sourceType: z.string(),
  targetType: z.string(),
  relation: z.string().optional(),
});

export type TypeEdge = z.infer<typeof typeEdgeSchema>;

export const insertTypeEdgeSchema = typeEdgeSchema.omit({});
export type InsertTypeEdge = z.infer<typeof insertTypeEdgeSchema>;
